import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/v2/components';

import { ETriggerActivityType } from '@/src/infrastructure/interfaces/IAutomation.interface';
import { BodyCreateRule } from '@/src/infrastructure/Protocol/ServiceInterfaces';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import {
  JourneyMessageCreatedResponse,
  RuleCreatedResponse,
} from '@/src/modules/AutomationModuleNew/interfaces/automation';

export const useCreateRule = (isRule: boolean = false) => {
  const { t } = useTranslation();
  const service = useService();
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ruleType, setRuleType] = useState<ETriggerActivityType | undefined>(
    isRule ? ETriggerActivityType.FLOWS : undefined
  );

  const handleToggleCreateModal = useCallback(() => {
    setCreateModalIsOpen((prev) => !prev);
  }, []);

  const createRule = useCallback(
    async (
      name: string
    ): Promise<
      | { isValidInput: true; data: RuleCreatedResponse | JourneyMessageCreatedResponse }
      | { isValidInput: false; message: string }
      | undefined
    > => {
      if (!service) return;

      setIsLoading(true);
      const body: BodyCreateRule = {
        name,
        ruleType,
      };
      const type = isRule ? 'email_rules' : 'email_templates';
      const response = await service.createRule(body, type);
      if (response.success) {
        toast({
          id: 'ruleCreated',
          title: t(`useCreateRule.${type}.success.title`),
          body: t(`useCreateRule.${type}.success.body`),
        });
        setIsLoading(false);
        if (isRule) {
          return { isValidInput: true, data: response.data as RuleCreatedResponse };
        }
        return { isValidInput: true, data: response.data as JourneyMessageCreatedResponse };
      } else if (response.data === 'RULE_ALREADY_EXISTS') {
        setIsLoading(false);
        return {
          isValidInput: false,
          message: t(`useCreateRule.${type}.error.RULE_ALREADY_EXISTS`),
        };
      }

      toast({
        id: 'ruleCreated',
        title: t(`useCreateRule.${type}.error.title`),
        body: t(`useCreateRule.${type}.error.body`),
        variant: 'error',
      });
      setIsLoading(false);
      return {
        isValidInput: false,
        message: t(`useCreateRule.${type}.error.body`),
      };
    },
    [isRule, ruleType, service, t]
  );

  return {
    createRule,
    isLoading,
    setRuleType,
    handleToggleCreateModal,
    createModalIsOpen,
    setIsLoading,
  };
};
